import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import { hasPermission } from '@/composables/usePermission'
import { useRouter } from '@core/utils/utils'
import i18n from '@/libs/i18n'
import { ref } from '@vue/composition-api'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import useAuditLog from '@/composables/useAuditLog'

export default function useInherentRiskEdit() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { route, router } = useRouter()
  const { updateItem, getItem } = useLambda()
  const { addAuditEntry } = useAuditLog()

  const userData = store.state?.userStore?.userData
  const userId = userData?.worker_id?.$oid
  const isSubmitting = ref(false)
  const collection = 'inherentAnalysis'
  const ariData = ref({
    scores: {},
    legalObligations: {
      ol_a: false,
      ol_b: false,
      ol_c: false,
      ol_d: false,
    },
  })
  const originalARIData = ref(null) // Store original data for comparison
  const ariId = route.value.params.id
  const query = { _id: oid(ariId), client_id: oid(userData.client.$oid) }

  const show = async () => {
    try {
      isSubmitting.value = true

      const result = await getItem({ collection, query })

      // Inicializar history si no existe (documentos legacy)
      if (result && !result.history) {
        result.history = []
      }

      ariData.value = result || {}
      // Store original data for change detection (deep copy)
      originalARIData.value = JSON.parse(JSON.stringify(result || {}))
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.ari_fetch_error') || 'Error al cargar ARI')
    } finally {
      isSubmitting.value = false
    }
  }

  // Build payload helper
  const buildPayload = (data, status = null) => {
    const payload = {
      treatment_id: data.treatment_id ? oid(data.treatment_id) : null,
      scores: data.scores || {},
      legalObligations: data.legalObligations || {
        ol_a: false,
        ol_b: false,
        ol_c: false,
        ol_d: false,
      },
      calculatedP: data.calculatedP || null,
      calculatedI: data.calculatedI || null,
      inherentRisk: data.inherentRisk || null,
      inherentLevel: data.inherentLevel || null,
      requiresEipdAuto: data.requiresEipdAuto || false,
      eipdOverride: data.eipdOverride ?? null,
      eipdOverrideJustification: data.eipdOverrideJustification || '',
      eipdFinal: data.eipdFinal || false,
    }

    // Only update status if explicitly provided
    if (status !== null) {
      payload.status = status
    }

    return payload
  }

  // Save as draft (for autosave and step navigation)
  // NO registra en audit log porque es autosave automático, no acción del usuario
  // IMPORTANTE: NO actualizar originalData aquí para que detecte cambios al hacer submit final
  const saveDraft = async (data) => {
    try {
      const payload = buildPayload(data)
      const action = { $set: payload }

      await updateItem({ collection, query, action })

      // NO actualizar originalData aquí - queremos comparar contra el estado cuando se cargó la página
    } catch (error) {
      console.error('Error saving draft:', error)
      throw error
    }
  }

  // Final update (marks as active)
  const update = async (data) => {
    if (!hasPermission('privacy-inherent-risk', 'edit')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      return
    }
    try {
      isSubmitting.value = true

      const payload = buildPayload(data, 'active')
      const action = { $set: payload }

      await updateItem({ collection, query, action })

      // Add audit log entry (sistema unificado)
      await addAuditEntry({
        collection,
        documentId: oid(ariId),
        originalData: originalARIData.value,
        updatedData: payload,
      })

      showSuccessMessage('ARI actualizado correctamente')
      router.push({ name: 'privacy-inherent-risk-list' })
    } catch (error) {
      console.log(error)
      showErrorMessage('Error al actualizar ARI')
    } finally {
      isSubmitting.value = false
    }
  }

  return {
    update,
    saveDraft,
    show,
    ariData,
    ariId,
    isSubmitting,
  }
}
